/**
 * Configuración centralizada de países - Single Source of Truth
 * Consolida enum, nombres, nacionalidades, códigos telefónicos, monedas, validaciones
 * @file country.constants.ts
 * @security Validaciones de documentos por país según regulaciones locales
 */

import { DropdopwnOptions } from '../interfaces/dropdown.interface';

/**
 * Enum de países soportados (códigos ISO 3166-1 alpha-3)
 */
export enum Country {
    USA = 'USA',
    ESP = 'ESP',
    DEU = 'DEU',
    FRA = 'FRA',
    ITA = 'ITA',
    NLD = 'NLD',
    PRT = 'PRT',
    COL = 'COL',
    CHL = 'CHL',
    ARG = 'ARG',
    ECU = 'ECU',
    MEX = 'MEX',
    PE = 'PE',
    BRA = 'BRA',
    GBR = 'GBR'
}

/**
 * Nombres completos de países
 */
export const COUNTRY_NAME: Record<string, string> = {
    [Country.DEU]: 'Alemania',
    [Country.ESP]: 'España',
    [Country.FRA]: 'Francia',
    [Country.ITA]: 'Italia',
    [Country.NLD]: 'Países Bajos',
    [Country.PRT]: 'Portugal',
    [Country.COL]: 'Colombia',
    [Country.CHL]: 'Chile',
    [Country.MEX]: 'México',
    [Country.ARG]: 'Argentina',
    [Country.USA]: 'Estados Unidos',
    [Country.BRA]: 'Brasil',
    [Country.PE]: 'Perú',
    [Country.ECU]: 'Ecuador',
    [Country.GBR]: 'Reino Unido'
};

/**
 * Nacionalidades por país
 */
export const COUNTRY_NATIONALITY: Record<string, string> = {
    [Country.DEU]: 'Alemana',
    [Country.ESP]: 'Española',
    [Country.FRA]: 'Francesa',
    [Country.ITA]: 'Italiana',
    [Country.NLD]: 'Neerlandesa',
    [Country.PRT]: 'Portuguesa',
    [Country.COL]: 'Colombiana',
    [Country.CHL]: 'Chilena',
    [Country.MEX]: 'Mexicana',
    [Country.ARG]: 'Argentina',
    [Country.USA]: 'Estadounidense',
    [Country.BRA]: 'Brasileña',
    [Country.PE]: 'Peruana',
    [Country.ECU]: 'Ecuatoriana',
    [Country.GBR]: 'Británica'
};

/**
 * Códigos telefónicos internacionales por país
 */
export const COUNTRY_CODE: Record<string, string> = {
    [Country.ITA]: '+39',
    [Country.PE]: '+51',
    [Country.ESP]: '+34',
    [Country.FRA]: '+33',
    [Country.PRT]: '+351',
    [Country.NLD]: '+31',
    [Country.DEU]: '+49',
    [Country.COL]: '+57',
    [Country.CHL]: '+56',
    [Country.MEX]: '+52',
    [Country.ARG]: '+54',
    [Country.USA]: '+1',
    [Country.BRA]: '+55',
    [Country.ECU]: '+593',
    [Country.GBR]: '+44'
};

/**
 * Clases CSS de iconos de banderas por país
 */
export const COUNTRY_ICONS: Record<string, string> = {
    [Country.DEU]: 'icon-de',
    [Country.ESP]: 'icon-es',
    [Country.FRA]: 'icon-fr',
    [Country.ITA]: 'icon-it',
    [Country.NLD]: 'icon-nl',
    [Country.PRT]: 'icon-pt',
    [Country.COL]: 'icon-co',
    [Country.CHL]: 'icon-cl',
    [Country.MEX]: 'icon-mx',
    [Country.PE]: 'icon-pe',
    [Country.ARG]: 'icon-ar',
    [Country.USA]: 'icon-us',
    [Country.BRA]: 'icon-br',
    [Country.ECU]: 'icon-ec',
    [Country.GBR]: 'icon-gb'
};

/**
 * Códigos de moneda (ISO 4217) por país
 */
export const COUNTRY_CURRENCY: Record<string, string> = {
    [Country.DEU]: 'EUR',
    [Country.ESP]: 'EUR',
    [Country.FRA]: 'EUR',
    [Country.ITA]: 'EUR',
    [Country.NLD]: 'EUR',
    [Country.PRT]: 'EUR',
    [Country.COL]: 'COP',
    [Country.CHL]: 'CLP',
    [Country.MEX]: 'MXN',
    [Country.PE]: 'PEN',
    [Country.ARG]: 'ARS',
    [Country.USA]: 'USD',
    [Country.BRA]: 'BRL',
    [Country.ECU]: 'USD',
    [Country.GBR]: 'GBP'
};

/**
 * Tipo de sistema de cuentas bancarias por país
 */
export const COUNTRY_ACCOUNT: Record<string, string> = {
    [Country.DEU]: 'IBAN',
    [Country.ESP]: 'IBAN',
    [Country.FRA]: 'IBAN',
    [Country.ITA]: 'IBAN',
    [Country.NLD]: 'IBAN',
    [Country.PRT]: 'IBAN',
    [Country.COL]: 'Swift/BIC',
    [Country.CHL]: 'Swift/BIC',
    [Country.MEX]: 'Swift/BIC',
    [Country.PE]: 'Swift/BIC',
    [Country.BRA]: 'Swift/BIC',
    [Country.ECU]: 'Swift/BIC'
};

/**
 * Interfaz para longitud de IBAN
 */
interface IbanLength {
    minLength: number;
    maxLength: number;
}

/**
 * Códigos ISO 3166-1 alpha-2 por país (usados en endpoints de API)
 */
export const COUNTRY_ISO2: Record<string, string> = {
    [Country.USA]: 'US',
    [Country.ESP]: 'ES',
    [Country.DEU]: 'DE',
    [Country.FRA]: 'FR',
    [Country.ITA]: 'IT',
    [Country.NLD]: 'NL',
    [Country.PRT]: 'PT',
    [Country.COL]: 'CO',
    [Country.CHL]: 'CL',
    [Country.ARG]: 'AR',
    [Country.ECU]: 'EC',
    [Country.MEX]: 'MX',
    [Country.PE]: 'PE',
    [Country.BRA]: 'BR',
    [Country.GBR]: 'GB'
};

/**
 * Longitud de IBAN por país (solo países con IBAN)
 */
export const COUNTRY_IBAN_LENGTH: Record<string, IbanLength> = {
    [Country.ESP]: { minLength: 24, maxLength: 35 },
    [Country.FRA]: { minLength: 27, maxLength: 27 },
    [Country.ITA]: { minLength: 27, maxLength: 27 },
    [Country.NLD]: { minLength: 18, maxLength: 18 },
    [Country.PRT]: { minLength: 25, maxLength: 25 },
    [Country.DEU]: { minLength: 22, maxLength: 22 },
    [Country.GBR]: { minLength: 22, maxLength: 22 }
};

/**
 * Número de dígitos a mostrar del código de país en teléfonos
 */
export const COUNTRY_SLICE_PHONE_NUMBER: Record<string, number> = {
    [Country.ITA]: 3,
    [Country.PE]: 3,
    [Country.ESP]: 3,
    [Country.FRA]: 3,
    [Country.PRT]: 4,
    [Country.NLD]: 3,
    [Country.DEU]: 3,
    [Country.COL]: 3,
    [Country.CHL]: 3,
    [Country.MEX]: 3,
    [Country.BRA]: 3,
    [Country.ECU]: 3,
    [Country.GBR]: 2,
    [Country.ARG]: 3,
    [Country.USA]: 2
};

/**
 * Formatos de ejemplo de IBAN por país
 */
export const COUNTRY_IBANS: Record<string, string> = {
    [Country.DEU]: 'DE00 0000 0000 0000 0000 00',
    [Country.ESP]: 'ES0000000000000000000000', //waiting for FEM solutions
    [Country.FRA]: 'FR00 0000 0000 0000 0000 0000 000',
    [Country.ITA]: 'IT00 0000 0000 0000 0000 0000 000',
    [Country.NLD]: 'NL00 ABNA 0000 0000 00',
    [Country.PRT]: 'PT00 0000 0000 0000 0000 0000 0',
    [Country.GBR]: 'GB00 BARC 0000 0000 0000 00'
};

/**
 * Interfaz para longitud de número telefónico
 */
interface PhoneLength {
    minLength: number;
    maxLength: number;
}

/**
 * Longitud permitida de números telefónicos por código de país
 */
export const COUNTRY_LENGTH_NUMBER: Record<string, PhoneLength> = {
    '+49': { maxLength: 11, minLength: 6 },
    '+34': { maxLength: 9, minLength: 9 },
    '+33': { maxLength: 9, minLength: 9 },
    '+39': { maxLength: 10, minLength: 9 },
    '+31': { maxLength: 10, minLength: 9 },
    '+351': { maxLength: 9, minLength: 9 },
    '+593': { maxLength: 10, minLength: 10 },
    '+1': { maxLength: 10, minLength: 10 },
    '+51': { maxLength: 9, minLength: 9 },
    '+54': { maxLength: 10, minLength: 10 },
    '+55': { maxLength: 11, minLength: 10 },
    '+56': { maxLength: 9, minLength: 9 },
    '+57': { maxLength: 10, minLength: 10 },
    '+52': { maxLength: 10, minLength: 10 },
    '+44': { maxLength: 10, minLength: 10 }
};

/**
 * Campos requeridos para envíos por región/país
 */
export const COUNTRYFIELDS: Record<string, readonly string[]> = {
    EU: [
        'Nombres y apellidos completos',
        'IBAN (Código internacional de cuenta europea)',
        'Correo electrónico',
        'Número de celular',
        'Estado, ciudad y dirección domiciliaria',
        'Tipo de documento (DNI/NIE, Documento tributario, Pasaporte) y el número de documento'
    ] as const,
    CHL: [
        'Nombres y apellidos completos',
        'Banco de destino',
        'Tipo y número de cuenta',
        'Número de celular',
        'Provincia, ciudad y dirección domiciliaria',
        'Rol Único Tributario y el número de documento'
    ] as const,
    COL: [
        'Nombres y apellidos completos',
        'Banco de destino',
        'Tipo y número de cuenta',
        'Número de celular',
        'Provincia, ciudad y dirección domiciliaria',
        'Tipo de documento (Número de Identificación Tributaria, Cédula de identidad , Cédula de extranjería, Pasaporte y el número de documento)'
    ] as const,
    MEX: [
        'Provincia, ciudad y dirección domiciliaria',
        'Nombres y apellidos completos',
        'Banco de destino',
        'Tipo y número de cuenta (CLABE)',
        'Número de celular',
        'Registro Federal de Contribuyentes (RFC)'
    ] as const,
    BRA: [
        'Nombres y apellidos completos',
        'Banco de destino',
        'Número de agencia',
        'Tipo y número de cuenta',
        'Número de celular',
        'Estado, ciudad y dirección domiciliaria',
        'Tipo de documento (CPF, CNPJ, RNE) y el número de documento'
    ] as const,
    PE: [
        'Nombres y apellidos completos',
        'Banco de destino',
        'Tipo y número de cuenta',
        'Número de celular',
        'Provincia, ciudad y dirección domiciliaria',
        'Documento Nacional de Identidad (DNI)'
    ] as const,
    ECU: [
        'Nombres y apellidos completos',
        'Banco de destino'
    ] as const
} as const;

export const COUNTRY_CODE_DROPDOWN_OPTIONS: DropdopwnOptions[] = [
    { value: 1, name: 'Italia', shortName: '+39', icon: 'assets/icons/countries/icon-italy.svg' },
    { value: 2, name: 'Perú', shortName: '+51', icon: 'assets/icons/countries/icon-peru.svg' },
    { value: 3, name: 'España', shortName: '+34', icon: 'assets/icons/countries/icon-spain.svg' },
    { value: 4, name: 'Francia', shortName: '+33', icon: 'assets/icons/countries/icon-france.svg' },
    { value: 5, name: 'Portugal', shortName: '+351', icon: 'assets/icons/countries/icon-portugal.svg' },
    { value: 6, name: 'Países Bajos', shortName: '+31', icon: 'assets/icons/countries/icon-netherlands.svg' },
    { value: 7, name: 'Alemania', shortName: '+49', icon: 'assets/icons/countries/icon-germany.svg' },
    { value: 8, name: 'Colombia', shortName: '+57', icon: 'assets/icons/countries/icon-colombia.svg' },
    { value: 9, name: 'Chile', shortName: '+56', icon: 'assets/icons/countries/icon-chile.svg' },
    { value: 10, name: 'México', shortName: '+52', icon: 'assets/icons/countries/icon-mexico.svg' },
    { value: 11, name: 'Brasil', shortName: '+55', icon: 'assets/icons/countries/icon-brazil.svg' },
    { value: 12, name: 'Ecuador', shortName: '+593', icon: 'assets/icons/countries/icon-ecuador.svg' },
    { value: 13, name: 'Reino Unido', shortName: '+44', icon: 'assets/icons/countries/icon-uk.svg' }
];